const MONTH_FORMAT = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  year: "numeric",
  month: "2-digit",
})

const DAY_FORMAT = new Intl.DateTimeFormat("ko-KR", {
  timeZone: "Asia/Seoul",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
})

const CATEGORY_RULES = [
  ["gantt", "간트차트", /gantt|간트|wbs/i],
  ["nodemaster", "노드마스터", /nodemaster|노드마스터/i],
  ["skyscribe", "스카이스크라이브", /skyscribe|스카이스크라이브|묵상/i],
  ["story", "스토리", /story|스토리/i],
  ["meal", "식사·이미지", /meal|식사|급식|image-request|이미지 요청/i],
  ["store", "스토어·광고", /store|market|스토어|마켓|광고|쿠팡/i],
  ["edu", "교육", /edu|교육|강의|수업/i],
  ["archive", "아카이브·제작", /archive|아카이브|3d|glb|모델링/i],
  ["music", "음악·캐스트", /music|cast|음악|캐스트|곡\s/i],
  ["town", "앤츠타운", /antstown|앤츠타운/i],
  ["talk", "앤츠톡·모바일", /antstalk|앤츠톡|안드로이드|android|nodeflow|채팅/i],
  ["maintenance", "운영·유지보수", /maintenance|정비|유지보수|장애|운영/i],
]

const dateOf = (task) => {
  const value = task.actualStartAt || task.startDate
  const date = value ? new Date(value) : null
  return date && !Number.isNaN(date.getTime()) ? date : null
}

export const journalCategory = (task) => {
  if (task.entryKind === "journal" || /^owner:.*:journal$/.test(task.actualKey || "")) {
    return ["journal", "업무일지"]
  }
  const text = `${task.actualKey || ""} ${task.name || ""}`
  return CATEGORY_RULES.find(([, , pattern]) => pattern.test(text)) || ["other", "기타 작업"]
}

const partsOf = (format, date) => {
  const parts = format.formatToParts(date)
  const pick = (type) => parts.find((part) => part.type === type)?.value.padStart(2, "0")
  return { year: pick("year"), month: pick("month"), day: pick("day") }
}

const monthOf = (task) => {
  const date = dateOf(task)
  if (!date) return ["unknown", "날짜 미상"]
  const { year, month } = partsOf(MONTH_FORMAT, date)
  return [`${year}-${month}`, `${year}년 ${Number(month)}월`]
}

const dayOf = (task) => {
  const date = dateOf(task)
  if (!date) return ["unknown", "날짜 미상"]
  const { year, month, day } = partsOf(DAY_FORMAT, date)
  return [`${year}-${month}-${day}`, `${Number(month)}월 ${Number(day)}일`]
}

const makeSummary = (id, label, leaves, children) => {
  const dates = leaves.flatMap((task) => [task.startDate, task.endDate])
    .map((value) => value && new Date(value))
    .filter((date) => date && !Number.isNaN(date.getTime()))
  return {
    id: `journal-group-${id}`,
    name: `${label} · ${leaves.length}건`,
    type: "summary",
    startDate: dates.length ? new Date(Math.min(...dates.map((date) => date.getTime()))) : null,
    endDate: dates.length ? new Date(Math.max(...dates.map((date) => date.getTime()))) : null,
    progress: leaves.length
      ? Math.round(leaves.reduce((sum, task) => sum + Number(task.progress || 0), 0) / leaves.length)
      : 0,
    isGroup: true,
    children,
  }
}

// 날짜 미상은 언제나 맨 뒤로 보내고, 나머지는 최근 것이 위로 오게 둔다.
const byIdDesc = ([a], [b]) =>
  a === b ? 0 : a === "unknown" ? 1 : b === "unknown" ? -1 : a > b ? -1 : 1

// 한 달 아래에 기록을 그대로 수백 개 늘어놓으면 타임라인이 하루짜리 막대로
// 쪼개져 읽히지 않는다. 날짜 묶음을 한 단계 끼워 하루를 막대 하나로 모은다.
const buildDays = (prefix, tasks) => {
  const days = new Map()
  tasks.forEach((task) => {
    const [dayId, dayLabel] = dayOf(task)
    if (!days.has(dayId)) days.set(dayId, { label: dayLabel, tasks: [] })
    days.get(dayId).tasks.push(task)
  })

  return [...days.entries()]
    .sort(byIdDesc)
    .map(([dayId, day]) => (
      // 기록이 하나뿐인 날은 묶음을 한 겹 더 두지 않고 기록을 그대로 올린다.
      day.tasks.length === 1
        ? day.tasks[0]
        : makeSummary(`${prefix}-${dayId}`, day.label, day.tasks, day.tasks)
    ))
}

export const groupAgentJournalTasks = (tasks) => {
  const categories = new Map()
  tasks.forEach((task) => {
    const [categoryId, categoryLabel] = journalCategory(task)
    const [monthId, monthLabel] = monthOf(task)
    if (!categories.has(categoryId)) categories.set(categoryId, { label: categoryLabel, months: new Map() })
    const category = categories.get(categoryId)
    if (!category.months.has(monthId)) category.months.set(monthId, { label: monthLabel, tasks: [] })
    category.months.get(monthId).tasks.push(task)
  })

  const order = [...CATEGORY_RULES.map(([id]) => id), "journal", "other"]
  return [...categories.entries()]
    .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
    .map(([categoryId, category]) => {
      const leaves = [...category.months.values()].flatMap((month) => month.tasks)
      const months = [...category.months.entries()]
        .sort(byIdDesc)
        .map(([monthId, month]) => makeSummary(
          `${categoryId}-${monthId}`,
          month.label,
          month.tasks,
          buildDays(`${categoryId}-${monthId}`, month.tasks),
        ))
      return makeSummary(categoryId, category.label, leaves, months)
    })
}
